import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';
import { validateResourceUrl } from '../utils/validation.js';

export interface AlibabaWan25I2iPreviewParams {
  input: {
    /**
     * 正向提示词，用来描述生成图像中期望包含的元素和视觉特点。支持中英文，长度不超过 2000 个字符
     * @length 0 - 2000
     * @example "将花卉连衣裙换成一件复古风格的蕾丝长裙，领口和袖口有精致的刺绣细节"
     */
    prompt: string;
    /**
     * 图像 URL 数组，最多支持 2 张图片。支持 HTTP/HTTPS URL 或 Base64 编码（系统会自动转换URL为Base64）。图像格式：JPEG、JPG、PNG、BMP、WEBP。图像分辨率：宽高范围均为 [384, 5000] 像素。文件大小：不超过 10MB
     * @example ["https://img.alicdn.com/imgextra/i2/O1CN01FuGdH91RenU9KPeri_!!6000000002137-2-tps-1344-896.png"]
     */
    images: string[];
    /**
     * 反向提示词，用来描述不希望在画面中看到的内容。支持中英文，长度不超过 500 个字符
     * @length 0 - 500
     * @example "低分辨率、错误、最差质量、低质量、残缺、多余的手指、比例不良"
     */
    negative_prompt?: string;
  };
  /**
   * 图像生成参数
   */
  parameters?: {
    /**
     * 生成图片的数量。取值范围为 1~4 张，默认为 4。测试阶段建议设置为 1
     * @default 4
     * @range 1 - 4
     * @example 1
     */
    n?: number;
    /**
     * 是否添加水印标识，水印位于图片右下角，文案固定为 AI 生成。默认 false 不添加水印
     * @default false
     */
    watermark?: boolean;
    /**
     * 随机数种子，取值范围是 [0, 2147483647]。如果不提供，则算法自动生成一个随机数作为种子。若需提升生成结果的可复现性，建议固定 seed 值
     * @range 0 - 2147483647
     * @example 42
     */
    seed?: number;
  };
}

export interface AlibabaWan25I2iPreviewResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_wan25_i2i_preview
 *
 * <p>通义万相 2.5 图生图预览版生成，支持单图编辑和多图参考生成（最多2张参考图）。系统会自动将图片URL转换为Base64格式发送给阿里云API，并对生成结果进行WebP转换和云存储上传处理</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaWan25I2iPreview } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaWan25I2iPreview({
 *   // 参数见 AlibabaWan25I2iPreviewParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaWan25I2iPreviewParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaWan25I2iPreview(
  params: AlibabaWan25I2iPreviewParams,
): Promise<AlibabaWan25I2iPreviewResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_wan25_i2i_preview',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaWan25I2iPreviewResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          // Validate URL before adding to results
          validateResourceUrl(resource, 'image');

          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
